// One source of truth for the public-site cookie preference.
//
// Necessary browser storage remains available for security, sign-in and local UI preferences.
// Optional marketing analytics is fail-closed: callers must see an explicit `analytics: true`
// decision before loading a provider or emitting an event.

export const COOKIE_CONSENT_VERSION = 1;
export const COOKIE_CONSENT_KEY = "vp360_cookie_consent_v1";
export const COOKIE_CONSENT_EVENT = "vp360:cookie-consent-change";
export const COOKIE_PREFERENCES_EVENT = "vp360:open-cookie-preferences";

const CONSENT_TTL_MS = 365 * 24 * 60 * 60 * 1000;

function isValidConsent(value) {
  if (!value || typeof value !== "object") return false;
  if (value.version !== COOKIE_CONSENT_VERSION || typeof value.analytics !== "boolean") return false;
  const expiresAt = Date.parse(value.expiresAt || "");
  return Number.isFinite(expiresAt) && expiresAt > Date.now();
}

/** Read the current, unexpired decision. Returns null until the visitor makes one. */
export function readCookieConsent() {
  if (typeof window === "undefined") return null;
  try {
    const parsed = JSON.parse(window.localStorage.getItem(COOKIE_CONSENT_KEY) || "null");
    if (isValidConsent(parsed)) return parsed;
    if (parsed) window.localStorage.removeItem(COOKIE_CONSENT_KEY);
  } catch {
    // Malformed or unavailable storage must fail closed and prompt again when the UI can render.
    try { window.localStorage.removeItem(COOKIE_CONSENT_KEY); } catch { /* noop */ }
  }
  return null;
}

/** Persist a versioned one-year decision and notify same-document analytics listeners. */
export function saveCookieConsent({ analytics }) {
  const decidedAt = new Date();
  const consent = {
    version: COOKIE_CONSENT_VERSION,
    analytics: analytics === true,
    decidedAt: decidedAt.toISOString(),
    expiresAt: new Date(decidedAt.getTime() + CONSENT_TTL_MS).toISOString(),
  };
  if (typeof window === "undefined") return consent;
  try { window.localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consent)); } catch { /* noop */ }
  try {
    window.dispatchEvent(new CustomEvent(COOKIE_CONSENT_EVENT, { detail: consent }));
  } catch { /* older/locked-down browsers still keep the local decision */ }
  return consent;
}

export function hasOptionalAnalyticsConsent() {
  return readCookieConsent()?.analytics === true;
}

/** Open the preference dialog from any public-site control, including the shared footer. */
export function requestCookiePreferences() {
  if (typeof window === "undefined") return;
  try { window.dispatchEvent(new Event(COOKIE_PREFERENCES_EVENT)); } catch { /* noop */ }
}
